import React from "react";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { getDBWithInit } from "~/lib/db";
import { Heading } from "~/components/ui/heading";
import { CoachCard } from "~/components/coach-card";
import { BreakdownIntro } from "~/components/feature/breakdown-intro";
import { MidListCTA } from "~/components/feature/mid-list-cta";
import { BottomCTA } from "~/components/feature/bottom-cta";
import { ExternalLink } from "lucide-react";
import { ButtonLink } from "~/components/ui/button";

export const getCcoData = createServerFn({ method: "GET" })
  .validator(z.object({ slug: z.string() }))
  .handler(async ({ data }) => {
    const { slug } = data;

    const db = await getDBWithInit();

    const ccoResult = await db.query.ccos.findFirst({
      where: (ccos, { eq }) => eq(ccos.slug, slug),
    });

    if (!ccoResult) {
      throw notFound();
    }

    const coachesWithCco = await db.query.coachesCcos.findMany({
      where: (coachesCcos, { eq }) => eq(coachesCcos.ccoId, ccoResult.id),
      with: { coach: { with: { profile: true } } },
    });

    const coaches = coachesWithCco
      .filter((cc) => cc.coach && cc.coach.profile)
      .map((cc) => cc.coach)
      .filter((coach) => {
        return (
          coach.fullName &&
          coach.profilePictureUrl &&
          coach.profile?.coachingTitle &&
          coach.profile?.description
        );
      })
      .map((coach) => ({
        ...coach,
        coachingTitle: coach.profile?.coachingTitle,
        description: coach.profile?.description,
        brandLogoUrl: coach.profile?.brandLogoUrl,
        brandName: coach.profile?.brandName,
        title: coach.profile?.title,
      }));

    return { cco: ccoResult, coaches, ccoSlug: slug };
  });

export const Route = createFileRoute("/_public/coaches/cco/$slug/")({
  loader: async ({ context, params }) => {
    await context.queryClient.ensureQueryData({
      queryKey: ["cco-data", params.slug],
      queryFn: () => getCcoData({ data: { slug: params.slug } }),
    });
    return {};
  },
  head: ({ params, loaderData: _loaderData }) => {
    const name = params.slug;
    return {
      title: `${name} Coaches`,
      meta: [
        {
          name: "description",
          content: `Discover coaches from ${name}. Quality and expertise assured by Dream Coach Match and ${name}.`,
        },
      ],
    };
  },
  component: CcoCoachesPage,
});

function CcoCoachesPage() {
  const { slug } = Route.useParams();

  const { data } = useQuery({
    queryKey: ["cco-data", slug],
    queryFn: () => getCcoData({ data: { slug } }),
  });

  if (!data) {
    return <div>Loading...</div>;
  }

  const { cco, coaches, ccoSlug } = data;
  const ccoName = cco?.name ?? "";

  return (
    <div className="mx-auto max-w-7xl px-4 py-12">
      {cco?.logoUrl && (
        <div className="mb-8 flex justify-center">
          <img
            src={cco.logoUrl}
            alt={`${ccoName} Logo`}
            className="h-24 w-auto max-w-[160px] rounded-md object-contain"
            width={160}
            height={96}
          />
        </div>
      )}
      <Heading
        preHeadline="Coaching Organization"
        headline={ccoName ? `${ccoName} Coaches` : "Coaches"}
        subheadline={
          ccoName
            ? `Find expert coaches associated with ${ccoName}.`
            : "Find expert coaches associated with leading organizations."
        }
      />
      <p className="mx-auto max-w-prose text-center">{cco.description}</p>
      <BreakdownIntro
        breakdownName={ccoName}
        breakdownTypeSlug={ccoSlug}
        ctaButtonTextTemplate="Find Your Ideal {} Coach"
        utmCampaignPrefix="cco_coaches"
      />
      {cco.website && (
        <div className="mt-4 text-center">
          <ButtonLink href={cco.website} target="_blank" variant="outline">
            Visit {cco.name} Website
            <ExternalLink className="ml-1.5 inline" />
          </ButtonLink>
        </div>
      )}
      <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {coaches.length > 0 ? (
          coaches.map((coach, i) => (
            <React.Fragment key={coach.id}>
              <CoachCard
                coachData={{
                  coach: {
                    id: coach.id,
                    username: coach.username,
                    fullName: coach.fullName,
                    membershipPlan: coach.membershipPlan,
                    profilePictureUrl: coach.profilePictureUrl,
                    isFeaturedCoach: coach.isFeaturedCoach,
                    isFoundingCoach: coach.isFoundingCoach,
                  },
                  profile: {
                    coachingTitle: coach.coachingTitle,
                    brandLogoUrl: coach.brandLogoUrl,
                    brandName: coach.brandName,
                    title: coach.title,
                  },
                }}
              />
              {i === 11 && (
                <MidListCTA
                  breakdownName={ccoName}
                  breakdownTypeSlug={ccoSlug}
                  preLinkText="Overwhelmed by choices?"
                  ctaLinkTextTemplate="Let us help you find the right {} coach!"
                  utmCampaignPrefix="cco_coaches_mid"
                />
              )}
            </React.Fragment>
          ))
        ) : (
          <p className="col-span-full text-center text-lg text-gray-500">
            No coaches found from this organization yet.
          </p>
        )}
      </div>
      <BottomCTA
        itemCount={coaches.length}
        itemTypePlural="coaches"
        breakdownName={ccoName}
        breakdownTypeSlug={ccoSlug}
        joinAsLinkUrl="/page/coach"
        joinAsLinkText="Join as a coach."
        mainCtaSubTextTemplate="Ready to connect with a coach from {}?"
        mainCtaButtonTextTemplate="Try our free Matchmaker tool"
        utmCampaignPrefix="cco_coaches_bottom"
      />
    </div>
  );
}
